import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    ArrowUpRight,
    ArrowDownRight,
    Loader2,
    BarChart3,
    ArrowRight,
    ReceiptText,
    Store,
    Download
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { fetchSalesPerformanceAnalysis, fetchSalesPerformanceExportCSV, fetchStoresWhereStaffAssigned } from '../apiHelper';
import Card from '../components/ui/Card';
import Modal from '../components/ui/Modal';
import ThermalReceiptPerf from './ThermalReceiptPerf';
import { useToast } from '../hooks/useToast';

const formatDate = (date) => date.toISOString().split('T')[0];

const getDefaultRanges = (passedStart, passedEnd) => {
    const today = new Date();
    const defaultAEnd = today;
    const defaultAStart = new Date();
    defaultAStart.setDate(today.getDate() - 30);

    const finalAStart = passedStart ? new Date(passedStart) : defaultAStart;
    const finalAEnd = passedEnd ? new Date(passedEnd) : defaultAEnd;

    const durationInMs = finalAEnd.getTime() - finalAStart.getTime();
    const finalBEnd = new Date(finalAStart.getTime() - (1000 * 60 * 60 * 24));
    const finalBStart = new Date(finalBEnd.getTime() - durationInMs);

    return {
        periodA: { start_date: formatDate(finalAStart), end_date: formatDate(finalAEnd) },
        periodB: { start_date: formatDate(finalBStart), end_date: formatDate(finalBEnd) }
    };
};

const OrderTable = ({ orders, emptyMsg, highlight, onViewReceipt }) => (
    <Card className={`overflow-hidden border-none shadow-sm ${highlight ? 'ring-1 ring-indigo-100' : 'opacity-80'}`}>
        <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
                <thead>
                    <tr className={`${highlight ? 'bg-indigo-50/50' : 'bg-gray-50'} border-b border-gray-100`}>
                        <th className="px-4 py-3 text-[10px] font-bold text-gray-500 uppercase">Order / Date</th>
                        <th className="px-4 py-3 text-[10px] font-bold text-gray-500 uppercase">Customer</th>
                        <th className="px-4 py-3 text-[10px] font-bold text-gray-500 uppercase text-right">Amount</th>
                        <th className="px-4 py-3 text-[10px] font-bold text-gray-500 uppercase text-center">Receipt</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 bg-white">
                    {orders.length === 0 ? (
                        <tr>
                            <td colSpan="4" className="px-4 py-10 text-center text-gray-400 text-sm italic">{emptyMsg}</td>
                        </tr>
                    ) : (
                        orders.map((order) => {
                            const fullName = `${order.customer?.first_name || ''} ${order.customer?.middle_name || ''} ${order.customer?.last_name || ''}`.trim() || 'Guest';
                            const totalAmount = Number(order.totalproduct || 0);
                            const cashAmt = Number(order.cash_sale_amount || 0);
                            const creditAmt = Number(order.credit_sale_amount || 0);

                            let badge = { text: 'Cash Sale', class: 'bg-emerald-50 text-emerald-600' };
                            if (cashAmt > 0 && creditAmt > 0) {
                                badge = { text: 'Split Sale', class: 'bg-indigo-50 text-indigo-600' };
                            } else if (creditAmt > 0) {
                                badge = { text: 'Credit Sale', class: 'bg-orange-50 text-orange-600' };
                            }

                            return (
                                <tr key={order.orders_id} className="hover:bg-gray-50 transition-colors group">
                                    <td className="px-4 py-3">
                                        <div className={`font-bold text-sm ${highlight ? 'text-indigo-900' : 'text-gray-700'}`}>
                                            #{order.orders_id}
                                        </div>
                                        <div className="text-[10px] text-gray-400">
                                            {new Date(order.placed_on).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="text-xs font-medium text-gray-800">
                                            {fullName}
                                        </div>
                                        <div className={`text-[9px] px-1.5 py-0.5 rounded w-fit uppercase font-bold mt-1 ${badge.class}`}>
                                            {badge.text}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="text-xs font-bold text-gray-900">
                                            ₦{totalAmount.toLocaleString()}
                                        </div>
                                        <div className="text-[9px] text-gray-400">{order.items?.length || 0} items</div>
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        <button
                                            onClick={() => onViewReceipt(order)}
                                            className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-full transition-all"
                                        >
                                            <ReceiptText size={18} />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </table>
        </div>
    </Card>
);

const SalesPerformanceReport = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { showToast } = useToast();
    const passedStart = location.state?.passedStartDate;
    const passedEnd = location.state?.passedEndDate;

    const [user, setUser] = useState(null);
    const [stores, setStores] = useState([]);
    const [selectedStore, setSelectedStore] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const defaults = useMemo(() => getDefaultRanges(passedStart, passedEnd), [passedStart, passedEnd]);
    const [periodA, setPeriodA] = useState(defaults.periodA);
    const [periodB, setPeriodB] = useState(defaults.periodB);
    
    const [data, setData] = useState({ 
        a: { summary: null, orders: [] }, 
        b: { summary: null, orders: [] }, 
        loading: false 
    });

    const [csvLoading, setCsvLoading] = useState(false);

    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (!lcUser) navigate('/login');
        else setUser(JSON.parse(lcUser));
    }, [navigate]);

    useEffect(() => {
        if (user) {
            fetchStoresWhereStaffAssigned(user).then(allStores => {
                const rtlStores = allStores.filter(s => s.storetype === 'RTL');
                setStores(rtlStores);
                if (rtlStores.length > 0) setSelectedStore(rtlStores[0]);
            });
        }
    }, [user]);

    const exportToCSV = async () => {
        setCsvLoading(true);
        try {
            const csvContent = await fetchSalesPerformanceExportCSV({
                start_date: periodA.start_date,
                end_date: periodA.end_date,
                store_id: selectedStore.store_id,
            });

            // Create blob from the raw CSV string
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            
            // Create temporary link and trigger download
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Sales_Export_${selectedStore.identifier}_${periodA.start_date}.csv`);
            document.body.appendChild(link);
            link.click();
            
            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            showToast("CSV exported successfully", "success");
        } catch (err) {
            const errorMessage = err.response?.data?.detail || "CSV Export failed";
            console.error(errorMessage, err);
            showToast(errorMessage, 'error');
        } finally {
            setCsvLoading(false);
        }
    };

    const loadAnalysis = async () => {
        if (!selectedStore) return;
        setData(prev => ({ ...prev, loading: true }));
        try {
            const [resultsA, resultsB] = await Promise.all([
                fetchSalesPerformanceAnalysis({ ...periodA, store_id: selectedStore.store_id }),
                fetchSalesPerformanceAnalysis({ ...periodB, store_id: selectedStore.store_id })
            ]);
            setData({ a: resultsA, b: resultsB, loading: false });
        } catch (err) {
            setData(prev => ({ ...prev, loading: false }));
        }
    };

    const handleViewReceipt = (order) => {
        setSelectedOrder(order);
        setIsModalOpen(true);
    };

    useEffect(() => { loadAnalysis(); }, [periodA, periodB, selectedStore]);

    const generatePDF = () => {
        const doc = new jsPDF();
        const summary = data.a.summary;
        const orders = data.a.orders;

        doc.setFontSize(20);
        doc.text("Sales Performance Report", 14, 22);
        
        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text(`Store: ${selectedStore?.identifier}`, 14, 30);
        doc.text(`Period: ${periodA.start_date} to ${periodA.end_date}`, 14, 35);

        // Financial Summary Table
        autoTable(doc, {
            startY: 45,
            head: [['Financial Metric', 'Value']],
            body: [
                ['Total Revenue', `NGN ${Number(summary.grand_total_product).toLocaleString()}`],
                ['Cash Received', `NGN ${Number(summary.total_cash_received).toLocaleString()}`],
                ['Credit Extended', `NGN ${Number(summary.total_credit_extended).toLocaleString()}`],
                ['Order Count', summary.total_orders],
            ],
            theme: 'striped',
            headStyles: { fillColor: [79, 70, 229] }
        });

        // 1. Product Summary Logic (Group by unique product name)
        const productMap = {};
        orders.forEach(order => {
            order.items?.forEach(item => {
                const name = item.catentry?.productname || 'Unknown Product';
                const qty = Number(item.quantity || 0);
                const total = Number(item.totalproduct || 0);
                if (!productMap[name]) {
                    productMap[name] = { qty: 0, total: 0 };
                }
                productMap[name].qty += qty;
                productMap[name].total += total;
            });
        });

        const productRows = Object.entries(productMap).map(([name, stats]) => [
            name,
            stats.qty,
            `NGN ${stats.total.toLocaleString()}`
        ]);

        autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 10,
            head: [['Product Name', 'Total Qty Sold', 'Total Revenue']],
            body: productRows,
            theme: 'grid',
            headStyles: { fillColor: [16, 185, 129] }, // Emerald color
            styles: { fontSize: 8 },
        });

        // Detailed Transactions Table
        const tableRows = orders.map(order => {
            const cashAmt = Number(order.cash_sale_amount || 0);
            const creditAmt = Number(order.credit_sale_amount || 0);
            
            let typeLabel = 'CASH';
            if (cashAmt > 0 && creditAmt > 0) typeLabel = 'SPLIT';
            else if (creditAmt > 0) typeLabel = 'CREDIT';

            return [
                order.orders_id,
                new Date(order.placed_on).toLocaleDateString(),
                `${order.customer?.first_name || ''} ${order.customer?.middle_name || ''} ${order.customer?.last_name || ''}`.trim() || 'Walk-in',
                order.items?.map(i => `${i.catentry.productname} (x${i.quantity})`).join('\n'),
                typeLabel,
                Number(order.totalproduct).toLocaleString()
            ];
        });

        autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 10,
            head: [['ID', 'Date', 'Customer', 'Products', 'Type', 'Total']],
            body: tableRows,
            styles: { fontSize: 8 },
            columnStyles: { 3: { cellWidth: 50 }, 5: { halign: 'right' } },
            headStyles: { fillColor: [31, 41, 55] }
        });

        doc.save(`Sales_Report_${selectedStore?.identifier}.pdf`);
    };

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            <div className="mb-8 flex flex-col md:flex-row md:items-end gap-4 justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <BarChart3 className="text-indigo-600" /> Performance Analysis
                    </h1>
                    <p className="text-gray-500 text-sm">Reviewing {selectedStore?.identifier || 'RTL Store'}</p>
                </div>
                
                <div className="flex flex-wrap items-center gap-2">
                    <div className="flex bg-white p-3 rounded-xl border shadow-sm items-center gap-2">
                        <div className="flex items-center gap-2 px-2 text-indigo-600">
                            <Store size={18} />
                            <select
                                className="text-xs font-bold border-none bg-transparent rounded p-0 focus:ring-0 cursor-pointer text-gray-900"
                                value={selectedStore?.store_id || ''}
                                onChange={(e) => setSelectedStore(stores.find(s => s.store_id == e.target.value))}
                            >
                                {stores.map(s => <option key={s.store_id} value={s.store_id}>{s.identifier}</option>)}
                            </select>
                        </div>
                        <div className="h-8 w-px bg-gray-200 mx-1"></div>
                        <DateRangeSelector label="Primary" range={periodA} setRange={setPeriodA} />
                        <div className="px-2 text-gray-300"><ArrowRight size={16} /></div>
                        <DateRangeSelector label="Comparison" range={periodB} setRange={setPeriodB} />
                    </div>
                    
                    <div className="flex gap-2">
                        <button 
                            onClick={exportToCSV}
                            disabled={data.loading || csvLoading || !data.a.summary}
                            className="bg-white hover:bg-gray-50 text-gray-700 border px-5 py-3 rounded-xl font-bold flex items-center gap-2 transition-all shadow-sm disabled:opacity-50">
                            {csvLoading ? <Loader2 className="animate-spin" size={18} /> : <Download size={18} />}
                            Export CSV
                        </button>
                        <button 
                            onClick={generatePDF}
                            disabled={data.loading || !data.a.summary}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-3 rounded-xl font-bold flex items-center gap-2 transition-all shadow-md disabled:opacity-50"
                        >
                            <Download size={18} /> Export PDF
                        </button>
                    </div>
                </div>
            </div>

            {data.loading ? (
                <div className="flex flex-col items-center justify-center py-20">
                    <Loader2 className="animate-spin text-indigo-600" size={40} />
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                        <ComparisonCard title="Revenue" valA={Number(data.a.summary?.grand_total_product || 0)} valB={Number(data.b.summary?.grand_total_product || 0)} isCurrency={true} />
                        <ComparisonCard title="Cash Received" valA={Number(data.a.summary?.total_cash_received || 0)} valB={Number(data.b.summary?.total_cash_received || 0)} isCurrency={true} />
                        <ComparisonCard title="Credit Extended" valA={Number(data.a.summary?.total_credit_extended || 0)} valB={Number(data.b.summary?.total_credit_extended || 0)} isCurrency={true} />
                        <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
                            <span className="text-[10px] uppercase font-bold text-gray-400">Orders (A)</span>
                            <div className="text-2xl font-black text-gray-900 mt-1">{data.a.summary?.total_orders || 0}</div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <div className="space-y-4">
                            <h2 className="font-bold text-gray-800 flex items-center gap-2 px-1 uppercase text-xs tracking-wider">
                                <div className="w-2 h-2 rounded-full bg-indigo-500"></div> Period A Results
                            </h2>
                            <OrderTable orders={data.a.orders} emptyMsg="No transactions found" highlight={true} onViewReceipt={handleViewReceipt} />
                        </div>
                        <div className="space-y-4">
                            <h2 className="font-bold text-gray-400 flex items-center gap-2 px-1 uppercase text-xs tracking-wider">
                                <div className="w-2 h-2 rounded-full bg-gray-300"></div> Comparison Results
                            </h2>
                            <OrderTable orders={data.b.orders} emptyMsg="No comparison data" highlight={false} onViewReceipt={handleViewReceipt} />
                        </div>
                    </div>
                </>
            )}

            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="Sales Record Preview" size="sm">
                <div className="bg-gray-50 -m-6 p-6">
                    <ThermalReceiptPerf order={selectedOrder} />
                </div>
            </Modal>
        </div>
    );
};

const DateRangeSelector = ({ label, range, setRange }) => (
    <div className="flex flex-col gap-1">
        <span className="text-[10px] uppercase font-extrabold text-gray-400 ml-1 tracking-tight">{label}</span>
        <div className="flex items-center gap-1">
            <input type="date" className="text-[11px] border-none bg-gray-50 rounded p-1 font-medium" value={range.start_date} onChange={(e) => setRange({ ...range, start_date: e.target.value })} />
            <span className="text-gray-300 font-bold">~</span>
            <input type="date" className="text-[11px] border-none bg-gray-50 rounded p-1 font-medium" value={range.end_date} onChange={(e) => setRange({ ...range, end_date: e.target.value })} />
        </div>
    </div>
);

const ComparisonCard = ({ title, valA, valB, isCurrency }) => {
    const diff = valA - valB;
    const percent = valB === 0 ? (valA > 0 ? 100 : 0) : (diff / valB) * 100;
    const format = (v) => isCurrency ? `₦${Math.round(v).toLocaleString()}` : v.toLocaleString();

    return (
        <Card className="p-5 border-none shadow-sm">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tight mb-2">{title}</p>
            <div className="flex items-baseline gap-2">
                <h3 className="text-xl font-black text-gray-900">{format(valA)}</h3>
                {valB !== 0 && (
                    <div className={`flex items-center text-[10px] font-bold ${percent >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {percent >= 0 ? <ArrowUpRight size={10} /> : <ArrowDownRight size={10} />}
                        {Math.abs(percent).toFixed(1)}%
                    </div>
                )}
            </div>
            <p className="text-[10px] text-gray-400 mt-2 font-medium italic">vs. {format(valB)} prev.</p>
        </Card>
    );
};

export default SalesPerformanceReport;